/**
 * Browser Tracing Module
 *
 * Provides detailed, structured console logging of every action in the browser.
 * Enabled only for authorized users (@airbrx.com email, RBAC permission, or manual override).
 *
 * Usage:
 *   import { trace } from '../lib/trace';
 *   trace.auth("Login initiated", { method: "google" });
 *   trace.api("GET /api/auth/status", { status: 200, duration: 45 });
 *   trace.startSpan("oauth-callback");
 *   trace.endSpan("oauth-callback");
 *
 * Console access:
 *   window.airbrxTrace.dump()       // Returns all buffered entries as JSON
 *   window.airbrxTrace.setLevel("verbose")
 *   window.airbrxTrace.enable()     // Manual override on
 *   window.airbrxTrace.disable()    // Manual override off
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TraceLevel = "off" | "error" | "info" | "debug" | "verbose";

type TraceCategory =
  | "auth"
  | "navigation"
  | "api"
  | "state"
  | "render"
  | "cookie"
  | "storage"
  | "lifecycle";

interface TraceEntry {
  timestamp: number;
  category: TraceCategory;
  level: TraceLevel;
  message: string;
  data?: Record<string, unknown>;
  duration_ms?: number;
  trace_id?: string;
}

// ---------------------------------------------------------------------------
// Configuration & State
// ---------------------------------------------------------------------------

const RING_BUFFER_SIZE = 500;

const LEVEL_ORDER: Record<TraceLevel, number> = {
  off: 0,
  error: 1,
  info: 2,
  debug: 3,
  verbose: 4,
};

let traceEnabled = false;
let traceLevel: TraceLevel = "info";
const ringBuffer: TraceEntry[] = [];
const activeSpans = new Map<string, number>();

// ---------------------------------------------------------------------------
// Ring Buffer
// ---------------------------------------------------------------------------

function pushEntry(entry: TraceEntry): void {
  if (ringBuffer.length >= RING_BUFFER_SIZE) {
    ringBuffer.shift();
  }
  ringBuffer.push(entry);
}

// ---------------------------------------------------------------------------
// Console Output
// ---------------------------------------------------------------------------

const CATEGORY_COLORS: Record<TraceCategory, string> = {
  auth: "#4CAF50",
  navigation: "#2196F3",
  api: "#FF9800",
  state: "#9C27B0",
  render: "#00BCD4",
  cookie: "#795548",
  storage: "#607D8B",
  lifecycle: "#E91E63",
};

function formatTimestamp(ts: number): string {
  return new Date(ts).toISOString().slice(11, 23);
}

function emitToConsole(entry: TraceEntry): void {
  const color = CATEGORY_COLORS[entry.category];
  const prefix = `%c[${formatTimestamp(entry.timestamp)}] [${entry.category.toUpperCase()}]`;
  const style = `color: ${color}; font-weight: bold`;

  const parts: unknown[] = [prefix, style, entry.message];
  if (entry.duration_ms !== undefined) {
    parts.push(`(${entry.duration_ms}ms)`);
  }
  if (entry.data && Object.keys(entry.data).length > 0) {
    parts.push(entry.data);
  }

  switch (entry.level) {
    case "error":
      console.error(...parts);
      break;
    case "info":
      console.info(...parts);
      break;
    case "debug":
      console.debug(...parts);
      break;
    case "verbose":
      console.debug(...parts);
      break;
  }
}

// ---------------------------------------------------------------------------
// Core Logging
// ---------------------------------------------------------------------------

function shouldLog(level: TraceLevel): boolean {
  if (!traceEnabled) return false;
  return LEVEL_ORDER[level] <= LEVEL_ORDER[traceLevel];
}

function log(
  category: TraceCategory,
  level: TraceLevel,
  message: string,
  data?: Record<string, unknown>,
  duration_ms?: number,
  trace_id?: string,
): void {
  const entry: TraceEntry = {
    timestamp: Date.now(),
    category,
    level,
    message,
    ...(data && { data }),
    ...(duration_ms !== undefined && { duration_ms }),
    ...(trace_id && { trace_id }),
  };

  pushEntry(entry);

  if (shouldLog(level)) {
    emitToConsole(entry);
  }
}

// ---------------------------------------------------------------------------
// Category Helpers
// ---------------------------------------------------------------------------

function createCategoryLogger(category: TraceCategory) {
  return (message: string, data?: Record<string, unknown>, level: TraceLevel = "info") => {
    log(category, level, message, data);
  };
}

// ---------------------------------------------------------------------------
// Gating
// ---------------------------------------------------------------------------

/**
 * Update trace gating based on the current user.
 * Call this after initializeAuth/refreshAuth sets the user.
 *
 * @param user - The user object (or null if logged out)
 * @param checkPermission - Function to check RBAC permission (avoids circular import with authStore)
 */
export function updateTraceGating(
  user: { email?: string } | null,
  checkPermission?: (permission: string) => boolean,
): void {
  const manualOverride = typeof localStorage !== "undefined"
    && localStorage.getItem("airbrx_trace") === "true";
  const isAirbrxEmail = user?.email?.endsWith("@airbrx.com") ?? false;
  const hasDiagPermission = checkPermission ? checkPermission("diagnostics:trace") : false;

  const wasEnabled = traceEnabled;
  traceEnabled = manualOverride || isAirbrxEmail || hasDiagPermission;

  if (traceEnabled && !wasEnabled) {
    log("lifecycle", "info", "Tracing enabled", {
      reason: manualOverride ? "manual_override" : isAirbrxEmail ? "airbrx_email" : "rbac_permission",
      email: user?.email ?? "unknown",
    });
  }
}

// ---------------------------------------------------------------------------
// Span Timing
// ---------------------------------------------------------------------------

function startSpan(name: string): void {
  activeSpans.set(name, performance.now());
  log("lifecycle", "debug", `Span started: ${name}`);
}

function endSpan(name: string, data?: Record<string, unknown>): void {
  const start = activeSpans.get(name);
  if (start === undefined) {
    log("lifecycle", "error", `Span not found: ${name}`);
    return;
  }
  activeSpans.delete(name);
  const duration_ms = Math.round(performance.now() - start);
  log("lifecycle", "info", `Span ended: ${name}`, data, duration_ms);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const trace = {
  auth: createCategoryLogger("auth"),
  navigation: createCategoryLogger("navigation"),
  api: createCategoryLogger("api"),
  state: createCategoryLogger("state"),
  render: createCategoryLogger("render"),
  cookie: createCategoryLogger("cookie"),
  storage: createCategoryLogger("storage"),
  lifecycle: createCategoryLogger("lifecycle"),

  startSpan,
  endSpan,

  /** Return all buffered trace entries */
  dump(): TraceEntry[] {
    return [...ringBuffer];
  },

  /** Set the trace level at runtime */
  setLevel(level: TraceLevel): void {
    traceLevel = level;
    log("lifecycle", "info", `Trace level set to: ${level}`);
  },

  /** Enable tracing (manual override) */
  enable(): void {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("airbrx_trace", "true");
    }
    traceEnabled = true;
    log("lifecycle", "info", "Tracing enabled via manual override");
  },

  /** Disable tracing (clears manual override) */
  disable(): void {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem("airbrx_trace");
    }
    traceEnabled = false;
    // Log to buffer even though console won't show it
    pushEntry({
      timestamp: Date.now(),
      category: "lifecycle",
      level: "info",
      message: "Tracing disabled",
    });
  },

  /** Check if tracing is currently enabled */
  isEnabled(): boolean {
    return traceEnabled;
  },

  /** Get current trace level */
  getLevel(): TraceLevel {
    return traceLevel;
  },
};

// ---------------------------------------------------------------------------
// Window Global
// ---------------------------------------------------------------------------

if (typeof window !== "undefined") {
  (window as unknown as Record<string, unknown>).airbrxTrace = {
    dump: () => trace.dump(),
    setLevel: (level: TraceLevel) => trace.setLevel(level),
    enable: () => trace.enable(),
    disable: () => trace.disable(),
    isEnabled: () => trace.isEnabled(),
  };
}

export default trace;
